/**
 * A service page's sync history (#11635 slice 1, Round 13, R42): the last
 * section of the page, under the documents. One row per sync, newest
 * first, every run the read returns: when it started (its date and clock in
 * the viewer's zone, then how long ago; R44), its outcome as a dot and
 * label, and how many documents it brought (R43; so far, while it runs).
 * No error text and no duration (Round 21, R71): a failed run reads as its
 * outcome alone, and the run's `error` stays on the wire for an engineer.
 *
 * A table at desktop width; on a phone one stacked block per sync, so
 * nothing runs past the edge.
 */
import React from "react";
import { XIcon } from "@phosphor-icons/react";
import type { PortalService } from "@/components/FpFundBrain/Manage/Connections/InvestorPortalsRow";
import { useViewerTimeZone } from "@/hooks/useViewerTimeZone";
import { formatInstantClock, type Temporal } from "@/utils/temporal";
import { RowPlaceholders, Section } from "./HomeSections";
import { relativeOf } from "./browser/portals";
import { rowActivationProps } from "./rowActivation";
import type { RunWindow } from "./browser/state";
import {
  runWindowOf,
  usePortalRuns,
  type PortalRun,
  type PortalRunOutcome,
} from "./portalRuns";

/** The outcome's dot and label, in the services' state vocabulary. */
const OUTCOME: Record<PortalRunOutcome, { label: string; dot: string }> = {
  succeeded: { label: "Succeeded", dot: "bg-green-500" },
  failed: { label: "Failed", dot: "bg-red-500" },
  running: { label: "Running", dot: "bg-amber-500" },
  cancelled: { label: "Cancelled", dot: "bg-gray-300" },
};

/** The Sync history section's id: the service's given-up line links to it
 *  (#13095 R-R1b). */
export const SYNC_HISTORY_ID = "portal-sync-history";

/** "1 could not be fetched" after a run's documents, when it could not
 *  keep some it tried (#13095 R-R1b); nothing when it kept them all. */
const RejectedClause: React.FC<{ run: PortalRun }> = ({ run }) =>
  run.rejected > 0 ? (
    <span className="text-[var(--fp-ink-3)]">
      {" · "}
      {run.rejected.toLocaleString()} could not be fetched
    </span>
  ) : null;

const thClass =
  "whitespace-nowrap px-3 py-2 text-left text-[11px] font-medium uppercase tracking-[0.04em] text-[var(--fp-ink-3)] border-b border-[var(--fp-border)]";
const tdClass = "px-3 py-2 text-[13px] text-[var(--fp-ink)] align-top";

const clockOf = (value: Temporal, zone: string): string => {
  switch (value.granularity) {
    case "instant":
      return formatInstantClock(value.at, zone);
    case "date":
      return value.day;
    default: {
      const unhandled: never = value;
      throw new Error(
        `unhandled temporal granularity: ${JSON.stringify(unhandled)}`
      );
    }
  }
};

/** When the run started (R44): its date and clock in the viewer's zone,
 *  then how long ago. A date-only start reads as the day, never a time. */
const Started: React.FC<{ run: PortalRun; zone: string }> = ({ run, zone }) => (
  <span className="[font-variant-numeric:tabular-nums]">
    <span className="text-[var(--fp-ink)]">
      {clockOf(run.started_at, zone)}
    </span>{" "}
    <span className="text-[var(--fp-ink-3)]">
      · {relativeOf(run.started_at)}
    </span>
  </span>
);

const Outcome: React.FC<{ run: PortalRun }> = ({ run }) => {
  const outcome = OUTCOME[run.outcome];
  return (
    <span className="inline-flex min-w-0 flex-wrap items-center gap-x-1.5">
      <span
        aria-hidden
        className={`inline-block h-2 w-2 shrink-0 rounded-full ${outcome.dot}`}
      />
      <span className="font-medium">{outcome.label}</span>
      {run.dry_run && (
        <span className="text-[12px] text-[var(--fp-ink-3)]">Dry run</span>
      )}
    </span>
  );
};

type RowProps = React.HTMLAttributes<HTMLElement> & {
  "data-outcome": PortalRunOutcome;
  "data-selected"?: "true";
};

/** What makes a history row the toggle (Round 15, R49 as amended): the
 *  whole row, every run, narrows the Documents section to what that run
 *  brought (0 documents is an honest answer); clicking the selected row
 *  again clears it. Enter or Space does the same from the keyboard. The
 *  row takes the Documents table's hover tone, and keeps it while it is the
 *  selected sync. A run whose start is not an instant (the route never
 *  sends one, the type admits it) has no window and is a plain row. */
const rowProps = (
  run: PortalRun,
  selectedRun: RunWindow | null,
  onToggle: (window: RunWindow) => void,
  className: string
): RowProps => {
  const runWindow = runWindowOf(run);
  const selected = selectedRun !== null && runWindow?.from === selectedRun.from;
  const base: RowProps = {
    "data-outcome": run.outcome,
    ...(selected ? { "data-selected": "true" as const } : {}),
    className,
  };
  if (runWindow === null) return base;
  return {
    ...base,
    className: `${className} cursor-pointer outline-none hover:bg-[var(--fp-hover-row)] focus-visible:outline focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-[var(--fp-ink-3)] ${
      selected ? "bg-[var(--fp-hover-row)]" : ""
    }`,
    // `aria-current` rather than `aria-selected`, which a list item may not
    // carry (the phone block is one).
    "aria-current": selected ? "true" : undefined,
    // A drag over a row's text does not toggle; a held key
    // toggles once.
    ...rowActivationProps(() => onToggle(runWindow)),
  };
};

/** The sync the Documents section is narrowed to, in its heading (Round
 *  15, R50): when it started, as the history row writes it, and a clear
 *  button. */
export const SyncPill: React.FC<{ run: RunWindow; onClear: () => void }> = ({
  run,
  onClear,
}) => {
  const zone = useViewerTimeZone();
  return (
    <span className="inline-flex h-6 items-center gap-1 self-center rounded-full border border-[var(--fp-border-strong)] bg-[var(--fp-surface)] pl-2.5 pr-1 text-[12px] font-medium leading-none text-[var(--fp-ink-2)] [font-variant-numeric:tabular-nums]">
      Synced {formatInstantClock(run.from, zone)}
      <button
        type="button"
        aria-label="Clear sync filter"
        onClick={onClear}
        className="inline-flex h-[18px] w-[18px] cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0 text-[var(--fp-ink-3)] hover:bg-[var(--fp-ink-wash)] hover:text-[var(--fp-ink)]"
      >
        <XIcon size={11} weight="bold" aria-hidden />
      </button>
    </span>
  );
};

export interface PortalRunsSectionProps {
  service: PortalService;
  /** Phone: one stacked block per sync instead of the table. */
  compact?: boolean;
  /** The sync the Documents section is narrowed to (R51), matched by its
   *  start; null when none is. */
  selectedRun: RunWindow | null;
  /** A run's row was clicked (R49 as amended), with that run's window. */
  onToggleRun: (window: RunWindow) => void;
}

const PortalRunsSection: React.FC<PortalRunsSectionProps> = ({
  service,
  compact = false,
  selectedRun,
  onToggleRun,
}) => {
  const zone = useViewerTimeZone();
  const { data, isPending, isError } = usePortalRuns(service.connection_id);
  if (isPending) {
    return (
      <Section title="Sync history" id={SYNC_HISTORY_ID}>
        <RowPlaceholders />
      </Section>
    );
  }
  if (isError) {
    return (
      <Section title="Sync history" id={SYNC_HISTORY_ID}>
        <p className="m-0 px-4 text-[13px] text-red-600">
          Could not load the sync history
        </p>
      </Section>
    );
  }
  if (data.runs.length === 0) {
    return (
      <Section title="Sync history" id={SYNC_HISTORY_ID}>
        <p className="m-0 px-4 text-[13px] text-[var(--fp-ink-3)]">
          No syncs yet
        </p>
      </Section>
    );
  }
  // Runs carry no id on the wire; the list is newest first and re-read
  // whole, so the position keys each row. The selected run is the one that
  // started when the narrowing does (R51; `rowProps`).
  return (
    <Section title="Sync history" id={SYNC_HISTORY_ID}>
      {compact ? (
        <ul className="m-0 list-none p-0">
          {data.runs.map((run, index) => (
            <li
              key={index}
              {...rowProps(
                run,
                selectedRun,
                onToggleRun,
                "flex flex-col gap-1 border-b border-[var(--fp-border)] px-4 py-2.5 text-[13px] text-[var(--fp-ink)] first:border-t"
              )}
            >
              <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
                <Outcome run={run} />
                <span className="text-[var(--fp-ink-3)]">·</span>
                <Started run={run} zone={zone} />
                <span className="text-[var(--fp-ink-3)]">·</span>
                <span className="text-[var(--fp-ink-2)] [font-variant-numeric:tabular-nums]">
                  {run.documents.toLocaleString()}{" "}
                  {run.documents === 1 ? "document" : "documents"}
                  <RejectedClause run={run} />
                </span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="px-4">
          <table className="w-full table-fixed border-collapse">
            <thead>
              <tr>
                <th scope="col" className={`${thClass} w-[44%]`}>
                  Started
                </th>
                <th scope="col" className={`${thClass} w-[28%]`}>
                  Outcome
                </th>
                <th scope="col" className={`${thClass} w-[28%]`}>
                  Documents
                </th>
              </tr>
            </thead>
            <tbody>
              {data.runs.map((run, index) => (
                <tr
                  key={index}
                  {...rowProps(
                    run,
                    selectedRun,
                    onToggleRun,
                    "border-b border-[var(--fp-border)]"
                  )}
                >
                  <td className={`${tdClass} whitespace-nowrap`}>
                    <Started run={run} zone={zone} />
                  </td>
                  <td className={tdClass}>
                    <Outcome run={run} />
                  </td>
                  <td
                    className={`${tdClass} text-[var(--fp-ink-2)] [font-variant-numeric:tabular-nums]`}
                  >
                    {run.documents.toLocaleString()}
                    <RejectedClause run={run} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
};

export default PortalRunsSection;
